import { Injectable, Inject } from '@angular/core';
import { AppHttpClientService } from '../shared/app-http-client.service';
import { Auction, AuctionInfo } from './auction.model';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { SESSION_STORAGE, StorageService } from 'ngx-webstorage-service';
import { first, map, skipWhile } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuctionService {
  private currentAuction = new BehaviorSubject<Auction>(null);
  private listAuctions = new BehaviorSubject<Auction[]>([]);
  private loaded = new ReplaySubject<void>(1);
  private auctionCount = 0;
  private selectedAuctionKey = 'pweb-auction-selected';

  constructor(private appHttpClient: AppHttpClientService, @Inject(SESSION_STORAGE) private storage: StorageService) {
    this.load();
  }

  getCurrent() {
    return this.currentAuction.asObservable().pipe(skipWhile((auction) => !auction));
  }

  getAll() {
    return this.listAuctions.asObservable();
  }

  count() {
    return this.auctionCount;
  }

  isLoaded() {
    return this.loaded.asObservable().pipe(first());
  }

  changeCurrent(auction: Auction) {
    this.currentAuction.next(auction);
    this.storage.set(this.selectedAuctionKey, auction.id);
  }

  private load() {
    this.appHttpClient.get<Auction[]>('auctions').subscribe((auctions) => {
      this.auctionCount = auctions.length;
      const auction = this.getCurrentFromStorageOrDefault(auctions);
      console.log('loaded auctions', auction);
      this.currentAuction.next(auction);
      this.listAuctions.next(auctions);
      this.loaded.next();
    });
  }

  private getCurrentFromStorageOrDefault(auctions: Auction[]) {
    const id = this.storage.get(this.selectedAuctionKey);
    if (id) {
      return auctions.find((auction) => auction.id === id) || auctions[0];
    }
    return auctions[0];
  }

  /**
   * Cuts the current bid by the specified amount
   * @param auctionId - The ID of the auction
   * @param amount - The amount to cut from the current bid
   * @returns Promise that resolves when the operation is complete
   */
  async cutBid(auctionId: string, amount: number): Promise<void> {
    return this.appHttpClient.post('auction/cut-bid', { auctionId, amount });
  }

  /**
   * Get the auction info
   * @param auctionId - The ID of the auction
   * @returns The auction info
   */
  public getAuctionInfo(auctionId: string) {
    return this.appHttpClient.get<AuctionInfo>('auction?auctionId=' + auctionId);
  }

  /**
   * Start the auction
   * @param auctionId - The ID of the auction
   * @returns Promise that resolves when the operation is complete
   */
  public startAuction(auctionId: string) {
    return this.appHttpClient.post('auction/start', { auctionId });
  }

  /**
   * Stop the auction
   * @param auctionId - The ID of the auction
   * @returns Promise that resolves when the operation is complete
   */
  public stopAuction(auctionId: string, options?: { closeScheduledBids?: boolean }) {
    const body: { auctionId: string; closeScheduledBids?: boolean } = { auctionId };
    if (options?.closeScheduledBids === true) {
      body.closeScheduledBids = true;
    }
    return this.appHttpClient.post('auction/stop', body);
  }
}
